import { useQuery } from '@tanstack/react-query';
import { Loader2, TrendingDown, TrendingUp } from 'lucide-react';
import { Card, CardContent, CardHeader, CardTitle, CardDescription } from '@/components/ui/card';
import { Badge } from '@/components/ui/badge';
import { getAnchorBiggestMovers, getOrgBiggestMovers } from '@/lib/admin/insights-api';

type Props =
  | { scope: 'survey'; surveyId: string }
  | { scope: 'org'; orgId: string };

export function BiggestMoversList(props: Props) {
  const q = useQuery({
    queryKey: ['admin', 'movers', props.scope, props.scope === 'survey' ? props.surveyId : props.orgId],
    queryFn: () =>
      props.scope === 'survey'
        ? getAnchorBiggestMovers(props.surveyId, 30).then((rows) =>
            rows.map((r) => ({ ...r, survey_name: null as string | null })),
          )
        : getOrgBiggestMovers(props.orgId, 30).then((rows) =>
            rows.map((r) => ({
              anchor_id: r.anchor_id,
              name: r.anchor_name,
              current_mean: r.current_mean,
              prior_mean: r.prior_mean,
              delta: r.delta,
              n_current: r.n_current,
              n_prior: r.n_prior,
              survey_name: r.survey_name,
            })),
          ),
  });

  return (
    <Card>
      <CardHeader>
        <CardTitle className="text-base font-heading">Biggest movers (30 days vs prior 30)</CardTitle>
        <CardDescription>Anchors with the largest shift. Only cells with k≥5 in both windows are shown.</CardDescription>
      </CardHeader>
      <CardContent className="space-y-2">
        {q.isLoading ? (
          <Loader2 className="h-5 w-5 animate-spin text-muted-foreground" />
        ) : !q.data || q.data.length === 0 ? (
          <p className="text-sm text-muted-foreground">No visible movers under the k≥5 floor.</p>
        ) : (
          q.data.slice(0, 8).map((m) => {
            const up = (m.delta ?? 0) >= 0;
            return (
              <div
                key={`${m.anchor_id}-${m.survey_name ?? ''}`}
                className="border border-border rounded-md p-3 flex items-center justify-between bg-card"
              >
                <div className="min-w-0">
                  <div className="font-medium truncate">{m.name}</div>
                  <div className="text-xs text-muted-foreground">
                    {m.survey_name ? <>{m.survey_name} · </> : null}
                    now {m.current_mean?.toFixed?.(2) ?? '–'} · prior {m.prior_mean?.toFixed?.(2) ?? '–'} ·
                    n {m.n_current}/{m.n_prior}
                  </div>
                </div>
                <Badge variant={up ? 'default' : 'destructive'} className="shrink-0">
                  {up ? <TrendingUp className="h-3 w-3 mr-1" /> : <TrendingDown className="h-3 w-3 mr-1" />}
                  {up ? '+' : ''}
                  {m.delta?.toFixed?.(2) ?? '0.00'}
                </Badge>
              </div>
            );
          })
        )}
      </CardContent>
    </Card>
  );
}
